import { Action, createReducer, on } from '@ngrx/store';
import { CartResultLineItemSelection } from 'entities/cart-result';
import * as CartActions from '../cart/cart.actions';
import { MyCartState } from './my-cart-state';
import * as MyCartActions from './my-cart.actions';

export const initialState: MyCartState = {
  cartItems: [],
  fixedVendor: false,
  hotFlags: [],
  autoSelectQuote: false,
  inboundFreight: null,
  vendorPartsPriceResult: []
};

export const myCartReducer = createReducer<MyCartState, Action>(
  initialState,
  on(CartActions.selectOpenCart, (state, {autoSelectQuote}) => ({...state, autoSelectQuote: autoSelectQuote?? false})),
  on(MyCartActions.getCartDataSuccess, (state, {cartItems}) => {
    const existingCartItems = state.cartItems ?? [];
    const updatedCartItems: CartResultLineItemSelection[] = cartItems?.map(incomingCartItem => {
      // Match on both cartItemId and isSplit to correctly preserve independent
      // selection state for client-side split items that share the same cartItemId
      const existingItem = existingCartItems.find(ei =>
        ei.cartItemId === incomingCartItem.cartItemId &&
        (ei.isSplit ?? false) === (incomingCartItem.isSplit ?? false)
      );
      const selected = existingItem?.selected;
      const inventory = existingItem?.inventory;
      // If the incoming cart item does not have vendor info, retain the existing vendor info.
      // Use || instead of ?? so that empty strings from the API (vendorCode: "") also fall
      // back to the existing value set by getVendorPricesSuccess. Without this, an empty
      // vendorCode wipes the locally-resolved vendor and disables the Confirm button.
      const vendorCode = incomingCartItem.vendorCode || existingItem?.vendorCode;
      const vendorName = incomingCartItem.vendorName || existingItem?.vendorName;
      const vendorBranchCode = incomingCartItem.vendorBranchCode || existingItem?.vendorBranchCode;
      // Preserve procurement-specific fields set by getVendorPricesSuccess / updateBuyoutCartItem
      // so that a concurrent getCartDataSuccess (e.g. from bin-location refresh) does not wipe them out.
      // These fields only exist on CartResultLineItemSelection, not on the incoming CartResultLineClient.
      const incomingAsSelection = incomingCartItem as Partial<CartResultLineItemSelection>;
      const knownPartBuyoutAmount = existingItem?.knownPartBuyoutAmount ?? incomingAsSelection.knownPartBuyoutAmount;
      const knownPartPrice = existingItem?.knownPartPrice ?? incomingAsSelection.knownPartPrice;
      const coreknownPartBuyoutAmount = existingItem?.coreknownPartBuyoutAmount ?? incomingAsSelection.coreknownPartBuyoutAmount;
      const coreknownPartPrice = existingItem?.coreknownPartPrice ?? incomingAsSelection.coreknownPartPrice;
      const isDefaultVendorSelected = existingItem?.isDefaultVendorSelected ?? incomingAsSelection.isDefaultVendorSelected;
      // Preserve locally-entered VIN, ESN, and CRC values
      const vin = existingItem?.vin ?? incomingCartItem.vin;
      const esn = existingItem?.esn ?? incomingCartItem.esn;
      const crc = existingItem?.crc ?? incomingCartItem.crc;
      // Only preserve locally-set hot flag state (set by updateCartItemHotFlag / clearCartItemHotFlag).
      // When hotFlagOverridden is not set, accept the API's values so normal store
      // dispatches (e.g. getCartDataSuccess with a new hotFlagCode) are not silently dropped.
      const hotFlagOverridden = existingItem?.hotFlagOverridden === true;
      const isHotFlag = hotFlagOverridden ? existingItem?.isHotFlag : incomingCartItem.isHotFlag;
      const hotFlagCode = hotFlagOverridden ? existingItem?.hotFlagCode : incomingCartItem.hotFlagCode;

      return ({...incomingCartItem, selected, inventory, vendorCode, vendorName, vendorBranchCode, knownPartBuyoutAmount, knownPartPrice, coreknownPartBuyoutAmount, coreknownPartPrice, isDefaultVendorSelected, vin, esn, crc, isHotFlag, hotFlagCode} as CartResultLineItemSelection);
    });
    const autoSelectQuote = updatedCartItems.length ? state.autoSelectQuote : false;
    return ({...state, cartItems: updatedCartItems || [], autoSelectQuote: autoSelectQuote });
  }),
  on(MyCartActions.selectAllItems, (state) => ({ ...state, cartItems: state.cartItems.map(x => ({...x, selected: true, branchCode: ''})) })),
  on(MyCartActions.unSelectAllItems, (state) => ({ ...state, cartItems: state.cartItems.map(x => ({...x, selected: false, branchCode: ''})) })),
  on(MyCartActions.getInventorySuccess, (state, { cartItemsWithInventory }: { cartItemsWithInventory: CartResultLineItemSelection[] }) =>
    ({
      ...state,
      // Match on both cartItemId and isSplit to avoid overwriting the IS item
      // with OOS data when both share the same cartItemId (client-side split)
      cartItems: state.cartItems.map(cartItem =>
        cartItemsWithInventory.find(ci =>
          ci.cartItemId === cartItem.cartItemId && ci.isSplit === cartItem.isSplit
        ) ?? cartItem
      )
    })
  ),
  on(MyCartActions.getHotFlagsSuccess, (state, {hotFlags}) => ({...state, hotFlags})),
  on(MyCartActions.getVendorPricesSuccess, (state, {cartItemsWithVendorPrices}) => ({
    ...state,
    cartItems: [...cartItemsWithVendorPrices, ...state.cartItems.filter(x => !x.selected)]
  })),
  on(MyCartActions.setVendorPartsPriceResult, (state, {vendorPartsPriceResult}) => ({
    ...state,
    vendorPartsPriceResult
  })),
  on(MyCartActions.searchVendorSuccess,
    (state, {partNumber, vendorCode, vendorName}) =>
      {
        const newItems = state.cartItems.map(cartItem => {
          if(cartItem.partNumber === partNumber){
            return {...cartItem, vendorCode, vendorName, isDefaultVendorSelected: cartItem.isDefaultVendorSelected == null? true: false};
          }
          return {...cartItem}
        });
        return ({...state, cartItems: [...newItems]});
      }),
  on(MyCartActions.toggleSelection, (state, {cartItemId, isSplit}) => {
    return {...state, cartItems: state.cartItems.map(
      cartItem => {
        // When isSplit is provided, match on both cartItemId and isSplit to allow
        // independent selection of in-stock vs out-of-stock split rows.
        const isMatch = cartItem.cartItemId === cartItemId &&
          (isSplit === undefined || isSplit === null || cartItem.isSplit === isSplit);
        if(isMatch){
          return {...cartItem, selected: !cartItem.selected, branchCode: '' }
        } else {
          return {...cartItem, branchCode: '' }
        }
      }
    )};
  }),
  on(MyCartActions.updateBuyoutCartItem, (state, {cartItem}) => {
    return {...state, cartItems: state.cartItems.map(
      ci => {
        if(ci.cartItemId === cartItem.cartItemId){
          return {...ci, knownPartPrice: cartItem.knownPartPrice, knownPartBuyoutAmount: cartItem.knownPartBuyoutAmount }
        }
        return ci;
      }
    )};
  }),
  on(MyCartActions.updateCartItemHotFlag, (state, {cartItemId, hotFlagCode, isSplit}) => {
    const cartItems = state.cartItems.map(x => {
      const idMatch = cartItemId === x.cartItemId;
      const splitMatch = isSplit === undefined || (x.isSplit ?? false) === isSplit;
      if(idMatch && splitMatch){
        return {...x, hotFlagCode: hotFlagCode, isHotFlag: true, hotFlagOverridden: true};
      }
      return x;
    });
    return ({...state, cartItems});
  }),
  on(MyCartActions.clearCartItemHotFlag, (state, {cartItemId, isSplit}) => {
    const cartItems = state.cartItems.map(x => {
      const idMatch = cartItemId === x.cartItemId;
      const splitMatch = isSplit === undefined || (x.isSplit ?? false) === isSplit;
      if(idMatch && splitMatch){
        return {...x, hotFlagCode: '', isHotFlag: false, hotFlagOverridden: true};
      }
      return x;
    });
    return ({...state, cartItems});
  }),
  on(MyCartActions.updateCartItemESN, (state, {cartItemId, esn}) => {
    const cartItems = state.cartItems.map(x => {
      if(cartItemId === x.cartItemId){
        return {...x, esn};
      }
      return x;
    });
    return ({...state, cartItems});
  }),
  on(MyCartActions.updateCartItemCRC, (state, {cartItemId, crc}) => {
    const cartItems = state.cartItems.map(x => {
      if(cartItemId === x.cartItemId){
        return {...x, crc};
      }
      return x;
    });
    return ({...state, cartItems});
  }),
  on(MyCartActions.updateCartItemVIN, (state, {cartItemId, vin}) => {
    const cartItems = state.cartItems.map(x => {
      if(cartItemId === x.cartItemId){
        return {...x, vin};
      }
      return x;
    });
    return ({...state, cartItems});
  }),
  on(CartActions.getFreightAndAddToCartSuccess, (state, {cart}) => ({...state, inboundFreight: cart.lineItems?.find(x => x.partNumber === "FREIGHT")}))
);
